import { useEffect, useRef } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import moment from "moment/moment"
import Delete from "pages/product/similar/delete"
import nProgress from "nprogress"
import { Pagination } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import useProductSimilarRepositories from "repositories/product-similar"
import Table from "components/table"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

export default function List() {
    // Transition
    const { title, transition, setTransition } = useOutletContext()

    // React router hooks
    const navigate = useNavigate()
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const searchInput = useRef()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const [searchTemp, setSearchTemp, submitSearch] = useDebouncedSearchParam({ search, searchParams, setSearchParams })

    const { data: similarData, isLoading: isLoadingSimilarData, mutate: mutateSimilarData } = useProductSimilarRepositories({
        page,
        search
    })

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'slide', reverse: false })
        }
    }, [])

    useEffect(() => {
        searchInput.current.focus()
    }, [search])

    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoadingSimilarData) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [similarData, isLoadingSimilarData])

    // Data list props
    const dataListHeaders = [
        {
            isAction: false,
            content: "Produk Sumber"
        },
        {
            isAction: false,
            content: "Jumlah Curated"
        },
        {
            isAction: false,
            content: "Diubah Pada"
        },
        {
            isAction: true,
            content: null
        }
    ]

    const dataListColumns = [
        {
            type: "default",
            bold: true,
            content: (value) => (
                <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-10 h-10 overflow-hidden rounded-lg bg-neutral-100">
                        {value.image && <img src={value.image} alt="" className="object-cover w-full h-full" />}
                    </div>
                    <div className="min-w-0">
                        <div className="font-medium truncate">{value.name}</div>
                        <div className="font-mono text-[11px] text-neutral-400 truncate">{value.product_model_id}</div>
                    </div>
                </div>
            )
        },
        {
            type: "default",
            bold: false,
            content: (value) => value.items_count
        },
        {
            type: "default",
            bold: false,
            content: (value) => value.updated_at ? moment(value.updated_at).format('MMMM D, YYYY') : '-'
        },
        {
            type: "action",
            content: (value) => (
                <>
                    <Link to={`${value.product_model_id}/edit`} state={{ back: title, from: location?.pathname, transition: 'slide' }} className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90">
                        <Pencil className="w-6 h-6" />
                    </Link>

                    <Delete data={value} onSuccess={() => mutateSimilarData()} />
                </>
            )
        }
    ]

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchTemp(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }} value={searchTemp} type="text" placeholder={`${lang.search} Produk Sumber`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <div>
                        <Link to="create" onClick={() => { setTransition({ type: 'slide', reverse: true }) }} state={{ back: title, from: location?.pathname, transition: { type: 'slide' } }} className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90">
                            <Plus className="w-4 h-4" stroke={1.5} />
                            <span>{lang.create}</span>
                        </Link>
                    </div>
                </div>
            </div>
            <Table isLoading={isLoadingSimilarData} data={similarData?.data ?? []} headers={dataListHeaders} columns={dataListColumns} key={(value) => value.product_model_id} isEmpty={similarData?.data.length === 0 && !search && !isLoadingSimilarData} isEmptyOnSearching={similarData?.data.length === 0 && search && !isLoadingSimilarData} />
            <Pagination links={similarData?.links} from={similarData?.from} to={similarData?.to} total={similarData?.total} />
        </div>
    )
}
